import { Component, Injector, OnInit } from '@angular/core';
import { DateUtil } from '../../../../../components/common/date-util/date-util';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';
import { WidgetBaseComponent } from '../../widgetBase';
import { PeriodOverPeriodKpiEntry, PeriodOverPeriodLayoutConfigPeriod } from './period-over-period-models';

/**
 * Period over Period widget
 */
@Component({
  selector: 'period-over-period-widget',
  templateUrl: './period-over-period-widget.component.html',
  styleUrls: ['./period-over-period-widget.component.scss']
})
export class PeriodOverPeriodWidgetComponent extends WidgetBaseComponent implements OnInit {
  /**
   * The available KPI entries
   */
  public availableKpiEntries: PeriodOverPeriodKpiEntry[] = null

  /**
   * The selected KPI entries
   */
  public selectedKpiEntries: PeriodOverPeriodKpiEntry[] = null;

  /**
   * The selected KPI entries for current layout in view mode
   */
  public selectedKpiEntriesForLayoutViewMode: PeriodOverPeriodKpiEntry[] = [];

  /**
   * The selected KPI entries for current layout in edit mode
   */
  public get selectedKpiEntriesForLayoutEditMode() {
    //includes added rows (with id === 0)
    return this.selectedKpiEntries.filter(kpi => kpi.layoutRestrictions === null || kpi.layoutRestrictions === this.currentLayout);
  }

  /**
   * The data displayed in widget
   */
  public displayData: any = null;

  /**
   * The periods
   */
  public get periods(): PeriodOverPeriodLayoutConfigPeriod[] {
    return this.widgetConfig.widgetSettings.layoutConfig;
  }

  /**
   * The period X
   */
  public periodX = null;

  /**
   * The period Y
   */
  public periodY = null;

  /**
   * The report Id
   */
  public get reportId() {
    return this.getReport();
  }

  /**
   * Table colspans sum 
   */
  allColspansCount = 114;

  /**
   * Label colspan
   */
  labelColspan = 30;

  /**
   * Button (add/remove) colspan
   */
  buttonColspan = 2;

  /**
   * Label colspan with buttons enabled (edit mode)
   */
  labelColspanWithButtons = 26;

  /**
   * Current valuation columns colspan
   */
  currrentValuationColspan = 0;

  /**
   * Period columns colspan
   */
  periodColspan = 0;

  /**
   * Comparision columns colspan
   */
  comparisionColspan = 12;

  /**
   * Should we hide a footnote
   */
  hideFootnote: boolean;

  /**
   * Constructor
   * @param injector Injector
   */
  constructor(injector: Injector) {
    super(injector, 'periodOverPeriod', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    //init kpi entries
    this.initAvailableKpiEntries();
    this.selectedKpiEntries = this.getWidgetSettingsOrDefault('selectedKpiEntries', ObjectUtil.deepCopy(this.availableKpiEntries));

    //adjust decimals if widget was already added to report (we changed decimal places in #20515)
    for (const selectedKpi of this.selectedKpiEntries) {
      const foundKpi = this.availableKpiEntries.find((availableKpi) => availableKpi.id === selectedKpi.id);
      if (foundKpi) {
        selectedKpi.decimalPlaces = foundKpi.decimalPlaces;
        selectedKpi.decimalPlacesComparison = foundKpi.decimalPlacesComparison;
        selectedKpi.unitLabelLeft = foundKpi.unitLabelLeft;
      }
    }

    //init periods
    const defaultLayoutConfig = this.getDefaultLayoutConfiguration();
    this.widgetConfig.widgetSettings.layoutConfig = this.getWidgetSettingsOrDefault('layoutConfig', defaultLayoutConfig);

    this.mockupData$ = this.widgetService.loadMockup('periodOverPeriod');

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('periodOverPeriod', this.getReport());
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data: any): any {
    data.periodValues = data.periodValues || [];
    data.periodValues.forEach(p => p.valuationDate = DateUtil.getDate(p.valuationDate));

    this.setKpisLabels();
    this.calculateDisplayData(data);
    return data;
  }

  /**
   * Layout change event handler
   * @param newLayout The new layout
   */
  public layoutChanged(newLayout: string): void {
    this.widgetConfig.widgetSettings.currentLayout = newLayout;

    this.calculateDisplayData(this.widgetData);
  }

  /**
   * Widget edit mode changed
   */
  protected editModeChanged() {
    this.calculateDisplayData(this.widgetData);
  }

  /**
   * Update display data
   */
  public updateDisplayData(): void {
    this.calculateDisplayData(this.widgetData);
  }

  /**
   * Calculate comparision
   */
  private getComparison(data: any, reportId: number): any {
    const currentPeriodData = ObjectUtil.deepCopy(data.periodValues.find(p => p.reportId === this.getReport()) || {});
    const periodData = ObjectUtil.deepCopy(data.periodValues.find(p => p.reportId === reportId) || {});

    const comparisonData = {};

    for (const kpiEntry of this.availableKpiEntries) {
      const currentValue: number = currentPeriodData[kpiEntry.valueProperty] || 0;
      const periodValue: number = periodData[kpiEntry.valueProperty] || 0;

      let value = 0;
      switch (kpiEntry.comparisonUnit) {
        case '%':
          if (periodValue === 0) {
            //edge cases:
            //a) from something to zero => display: -100%
            //b) both zero => display: 0%
            value = currentValue ? -100 : 0;
          }
          else {
            value = (currentValue / periodValue - 1) * 100;
          }
          break;
        case 'years':
          value = currentValue - periodValue;
          break;
        case 'bps':
          value = (currentValue - periodValue) * 100;
          break;
        default:
          break;
      }

      comparisonData[kpiEntry.valueProperty] = value;
    }

    return comparisonData;
  }

  /**
   * Calculate display data
   */
  private calculateDisplayData(data: any) {
    const currentPeriod = data.periodValues.find(p => p.reportId === this.getReport()) || {};

    this.periodX = this.periods.find(p => p.isPeriodX);
    this.periodY = this.periods.find(p => p.isPeriodY);

    const displayData: any = {
      currentPeriod: ObjectUtil.deepCopy(currentPeriod),
      comparisonPeriodX: null,
      comparisonPeriodY: null//if null it won't be shown in table
    };

    for (const period of this.periods) {
      if (period.visible) {
        displayData[period.reportId] = ObjectUtil.deepCopy(data.periodValues.find(p => p.reportId === period.reportId) || {});
      }

      if (period.isPeriodX) {
        displayData.comparisonPeriodX = this.getComparison(data, period.reportId)
      }

      if (period.isPeriodY) {
        displayData.comparisonPeriodY = this.getComparison(data, period.reportId)
      }
    }

    //as periods are (unfortunatelly) array, we keep track should we hide footnote on first element (ni fact we sync on all elements)
    this.hideFootnote = this.periods[0]?.hideFootnote;
    this.setKpisLabels();

    this.displayData = displayData;

    this.selectedKpiEntriesForLayoutViewMode = this.selectedKpiEntries.filter(kpi => kpi.id !== 0 && (!kpi.layoutRestrictions || kpi.layoutRestrictions === this.currentLayout));

    this.calculateColspans();
  }

  /**
   * Calculate table colspans
   */
  private calculateColspans() {
    const visibleComparisions = this.displayData.comparisonPeriodY ? 2 : 1;
    const flexibleColspans = this.allColspansCount - this.labelColspan - (visibleComparisions * this.comparisionColspan);

    const visiblePeriods = this.periods.filter(p => p.visible).length;

    const periodColspan = Math.floor(visiblePeriods === 0 ? flexibleColspans : flexibleColspans / (visiblePeriods + 1));//aditional one for current valuation

    this.currrentValuationColspan = flexibleColspans - visiblePeriods * periodColspan;
    this.periodColspan = periodColspan;
  }

  /**
  * Checks if topic selection is disabled
  * @param self this (because this context is lost here)
  * @param entry Current kpi entry in dropdown
  */
  public wrapIsTopicSelectionDisabled(self: PeriodOverPeriodWidgetComponent, entry: PeriodOverPeriodKpiEntry) {
    return (itemArgs: { dataItem: PeriodOverPeriodKpiEntry; index: number }): boolean => {
      if (entry.id === itemArgs.dataItem.id) {
        //The current entry can always be selected
        return false;
      }

      const alreadySelected = self.selectedKpiEntries.filter((kpi) => kpi.id === itemArgs.dataItem.id).length > 0;
      const availableForLayout = itemArgs.dataItem.layoutRestrictions === null || itemArgs.dataItem.layoutRestrictions === this.currentLayout;

      if (itemArgs.dataItem.id === 0 || alreadySelected || !availableForLayout) {
        //Only non-empty and non-selected entries (available for currentLayout) can be picked
        return true;//DISABLED
      }

      return false;
    }
  }

  /**
   * Kpi change event handler
   * @param entry kpi entry
   */
  public onKpiChanged(entry: PeriodOverPeriodKpiEntry, e) {
    const matchingEntry = this.availableKpiEntries.find((en) => en.label === entry.label);
    this.assignKpi(entry, matchingEntry);
  }

  /**
   * Assign Kpi entry
   * @param entry kpi entry
   * @param matchingEntry matching kpi entry
   */
  private assignKpi(entry: PeriodOverPeriodKpiEntry, matchingEntry: PeriodOverPeriodKpiEntry) {
    entry.id = matchingEntry.id;
    entry.label = matchingEntry.label;
    entry.translationKey = matchingEntry.translationKey;
    entry.valueProperty = matchingEntry.valueProperty;
    entry.layoutRestrictions = matchingEntry.layoutRestrictions;
    entry.unit = matchingEntry.unit;
    entry.comparisonUnit = matchingEntry.comparisonUnit;
    entry.decimalPlaces = matchingEntry.decimalPlaces;
    entry.decimalPlacesComparison = matchingEntry.decimalPlacesComparison;
    entry.unitLabelLeft = matchingEntry.unitLabelLeft;
  }

  /**
   * Add new kpi row
   * @param previousRow previous kpi row
   */
  public addRow(previousRow: PeriodOverPeriodKpiEntry): void {
    let index;
    for (index = 0; index < this.selectedKpiEntries.length; ++index) {
      if (this.selectedKpiEntries[index] === previousRow) {
        break;
      }
    }

    const newEntry: PeriodOverPeriodKpiEntry = {
      id: 0,
      label: '',
      translationKey: '',
      valueProperty: '',
      layoutRestrictions: null,
      unit: null,
      comparisonUnit: null,
      decimalPlaces: 0,
      decimalPlacesComparison: 0
    };

    this.selectedKpiEntries.splice(index, 0, newEntry);
  }

  /**
   * Remove kpi row
   * @param row kpi row to remove
   */
  public removeRow(row: PeriodOverPeriodKpiEntry) {
    //we have to leave at least 1 entry, otherwise it would be impossible to add new rows
    if (this.selectedKpiEntriesForLayoutEditMode.length > 1) {
      const idx = this.selectedKpiEntries.indexOf(row);
      this.selectedKpiEntries.splice(idx, 1);
    }
  }

  /**
   * Sets kpi labels
   */
  private setKpisLabels(): void {
    const labels = [
      {
        oid: 1,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelMarketValueRounded') //"Market Value (rounded)"
      },
      {
        oid: 2,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelLettableArea') //"Lettable Area",
      },
      {
        oid: 3,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelValuePerLettableArea') //"Value per lettable area"
      },
      {
        oid: 4,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelOccupancyRateOnIncome') //"Occupancy rate (on income)"
      },
      {
        oid: 5,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelWault') //"WAULT (years)"
      },
      {
        oid: 6,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelEffectiveContractualGrossRevenue') //"Effective (Contractual) Gross Revenue*"
      },
      {
        oid: 7,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelTotalOperatingExpenses') //"Total Operating Expenses*"
      },
      {
        oid: 8,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelNetOperatingIncome') //"Net Operating Income*"
      },
      {
        oid: 9,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelGrossMarketRentalValue') //"Market Rental Value (Gross)"
      },
      {
        oid: 10,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelTotalCapitalCostsYear1') //"Total Capital Costs (Year 1)"
      },
      {
        oid: 11,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelExitCapRate') //"Exit Cap Rate"
      },
      {
        oid: 12,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelDiscountRate') //"Discount Rate"
      },
      {
        oid: 13,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelEquivalentYield') //"Equivalent Yield"
      },
      {
        oid: 14,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelNetInitialYield') //"Net Initial Yield"
      },
      {
        oid: 15,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelGrossInitialYield') //"Gross Initial Yield"
      },
      {
        oid: 16,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelNetReversionaryYield') //"Net Reversionary Yield"
      },
      {
        oid: 17,
        description: this.reportDesignerTranslator.getString('periodOverPeriod.labelGrossReversionaryYield') //"Gross Reversionary Yield"
      }
    ];

    const processLabel = (label: string) => {
      if (this.hideFootnote && label) {
        //if footnote is hidden also remove * from labels
        return label?.replace('*', '');
      } else {
        return label;
      }
    }

    this.availableKpiEntries.forEach(kpi => kpi.label = processLabel(labels.find(l => l.oid === kpi.id)?.description));
    this.selectedKpiEntries.forEach(kpi => kpi.label = processLabel(labels.find(l => l.oid === kpi.id)?.description));
  }

  /**
   * Initializes available kpis
   */
  private initAvailableKpiEntries(): void {
    /*
      labels will be set from dictionary
    */
    this.availableKpiEntries = [
      {
        id: 1,
        label: '',
        translationKey: 'labelMarketValueRounded',
        valueProperty: 'marketValueRounded',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 2,
        label: '',
        translationKey: 'labelLettableArea',
        valueProperty: 'lettableArea',
        layoutRestrictions: null,
        unit: this.unitOfArea,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1
      },
      {
        id: 3,
        label: '',
        translationKey: 'labelValuePerLettableArea',
        valueProperty: 'valuePerLettableArea',
        layoutRestrictions: null,
        unit: this.currency + '/' + this.unitOfArea,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1
      },
      {
        id: 4,
        label: '',
        translationKey: 'labelOccupancyRateOnIncome',
        valueProperty: 'occupancyRateOnIncome',
        layoutRestrictions: null,
        unit: '%',
        comparisonUnit: '%',
        decimalPlaces: 2,
        decimalPlacesComparison: 1
      },
      {
        id: 5,
        label: '',
        translationKey: 'labelWault',
        valueProperty: 'wault',
        layoutRestrictions: null,
        unit: 'years',
        comparisonUnit: 'years',
        decimalPlaces: 1,
        decimalPlacesComparison: 1
      },
      {
        id: 6,
        label: '',
        translationKey: 'labelEffectiveContractualGrossRevenue',
        valueProperty: 'effectiveContractualGrossRevenue',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 7,
        label: '',
        translationKey: 'labelTotalOperatingExpenses',
        valueProperty: 'totalOperatingExpenses',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 8,
        label: '',
        translationKey: 'labelNetOperatingIncome',
        valueProperty: 'netOperatingIncome',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 9,
        label: '',
        translationKey: 'labelGrossMarketRentalValue',
        valueProperty: 'grossMarketRentalValue',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 10,
        label: '',
        translationKey: 'labelTotalCapitalCostsYear1',
        valueProperty: 'totalCapitalCostsYear1',
        layoutRestrictions: null,
        unit: this.currency,
        comparisonUnit: '%',
        decimalPlaces: 0,
        decimalPlacesComparison: 1,
        unitLabelLeft: true
      },
      {
        id: 11,
        label: '',
        translationKey: 'labelExitCapRate',
        valueProperty: 'dcfExitCapRate',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 12,
        label: '',
        translationKey: 'labelDiscountRate',
        valueProperty: 'dcfDiscountRate',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 13,
        label: '',
        translationKey: 'labelEquivalentYield',
        valueProperty: 'tradValEquivalentYield',
        layoutRestrictions: 'Trad Val',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 14,
        label: '',
        translationKey: 'labelNetInitialYield',
        valueProperty: 'dcfNetInitialYield',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 15,
        label: '',
        translationKey: 'labelGrossInitialYield',
        valueProperty: 'dcfGrossInitialYield',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 16,
        label: '',
        translationKey: 'labelNetReversionaryYield',
        valueProperty: 'dcfNetReversionaryYield',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      },
      {
        id: 17,
        label: '',
        translationKey: 'labelGrossReversionaryYield',
        valueProperty: 'dcfGrossReversionaryYield',
        layoutRestrictions: 'DCF',
        unit: '%',
        comparisonUnit: 'bps',
        decimalPlaces: 2,
        decimalPlacesComparison: 0
      }
    ];
  }

  /**
   * Returns default layout configuration
   */
  private getDefaultLayoutConfiguration() {
    const defaultConfig: PeriodOverPeriodLayoutConfigPeriod[] = [
      {
        id: 1,
        label: 'Period 1',//'Q3 2022',
        reportId: null,//395048,
        visible: true,
        //colspan: 3,
        isPeriodX: true,
        isPeriodY: false
      },
      {
        id: 2,
        label: 'Period 2',//'Q1 2023',
        reportId: null,//395050,
        visible: true,
        //colspan: 3,
        isPeriodX: false,
        isPeriodY: false
      },
      {
        id: 3,
        label: 'Period 3',//'Q2 2023',
        reportId: null,//395052,
        visible: true,
        //colspan: 3,
        isPeriodX: false,
        isPeriodY: false
      },
      {
        id: 4,
        label: 'Period 4',//'Q3 2023',
        reportId: null,//395054,
        visible: true,
        //colspan: 3,
        isPeriodX: false,
        isPeriodY: false
      }
    ];

    return defaultConfig;
  }
}
